import { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { chiudiListini } from "@/lib/listini";
import { api } from "@/lib/api";

export const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

/**
 * Lo stato del profilo, dichiarato invece che dedotto.
 *
 * Prima `profile` era `null` in tre situazioni diverse — non c'è sessione, la
 * chiamata è in volo, la chiamata è fallita — e da fuori erano indistinguibili.
 * Chi disegnava doveva scegliere fra indovinare («è un venditore») e mentire
 * («sto caricando», per sempre). Adesso la differenza la dice il contesto, che
 * è l'unico posto che la sa.
 *
 * `non-disponibile` non distingue l'errore di rete dal 200 senza profilo di
 * proposito: per chi guarda lo schermo l'esito è lo stesso — il ruolo non si sa.
 */
export const PROFILO = {
  INATTIVO: "inattivo", //        non c'è sessione: non c'è niente da sapere
  IN_CORSO: "in-corso", //        la chiamata è partita e non è ancora tornata
  DISPONIBILE: "disponibile", //  la risposta è arrivata
  NON_DISPONIBILE: "non-disponibile", // finita male, o tornata senza ciò che serviva
};

/* Lo stesso vocabolario vale per il secondo fattore, e per la stessa ragione.
   `aal` e `factors` partivano a `null` e `{ totp: [] }`: finché erano quelli,
   «non so se ha il secondo fattore» si leggeva come «non ce l'ha», e la guardia
   MFA non scattava. È lo stesso difetto del ruolo indovinato, su un cancello. */
export const MFA = PROFILO;

/* Fra un tentativo e l'altro di leggere il secondo fattore: 1 s, poi 2 s. */
export const ATTESA_RIPROVA_MS = 1000;

/* Se auth/v1/user cade, i fattori si leggono dalla sessione locale: Supabase la riscrive a
   ogni accesso e a ogni verifica del codice, ed è la stessa da cui
   getAuthenticatorAssuranceLevel ricava `nextLevel`. Così chi ha il codice arriva al codice
   anche col cantiere lento, e dopo averlo inserito entra (prima restava fermo sul livello
   vecchio). Senza fattori nella sessione non si sa niente: resta l'errore. */
async function fattoriDellaSessione(caduta) {
  const { data } = await supabase.auth.getSession();
  const tutti = data?.session?.user?.factors;
  if (!tutti?.length) return caduta;
  const totp = tutti.filter((f) => f.factor_type === "totp" && f.status === "verified");
  return { data: { all: tutti, totp, phone: [] }, error: null };
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profiloStato, setProfiloStato] = useState(PROFILO.INATTIVO);
  const [mfaStato, setMfaStato] = useState(MFA.INATTIVO);
  const [aal, setAal] = useState(null); // { currentLevel, nextLevel }
  const [factors, setFactors] = useState({ totp: [] });
  const [loading, setLoading] = useState(true);

  /* Contatore delle richieste: se due `/me` si accavallano (cambio sessione,
     rientro da uno sfondo), solo l'ultima ha diritto di scrivere. Senza, una
     risposta vecchia può atterrare dopo quella nuova e lasciare a schermo il
     profilo dell'utente precedente. */
  const richiesta = useRef(0);
  const richiestaMfa = useRef(0);
  /* «L'abbiamo saputo almeno una volta in questa sessione?»
     Serve a distinguere la PRIMA valutazione dalla rivalidazione. Il buco che
     stiamo chiudendo è la prima: lì non sappiamo niente e non si può decidere.
     Una rivalidazione che fallisce è un'altra cosa — una verità l'avevamo, e
     tenerla non è inventare, è ricordare. Senza questa distinzione ogni volta
     che le Impostazioni richiamano `refreshAal` per attivare o togliere il
     TOTP, `ProtectedRoute` sbiancherebbe la pagina intera su «Caricamento…». */
  const profiloNoto = useRef(false);
  const mfaNoto = useRef(false);

  /* Il `try` non c'era, e senza, una rete che cade qui produce un rifiuto di
     promessa non gestito e lascia `factors` fermo al valore precedente.
     `mfaStato` serve a chi decide un cancello: `factors` a `{ totp: [] }` non
     vuol dire «non ha il secondo fattore», vuol dire che non lo sappiamo. */
  /* Le due chiamate di Supabase non lanciano: un errore torna in `error`, con `data`
     nullo. `listFactors` passa da auth/v1/user, e quando quella cade (il cantiere lento
     del 27-10) `data` nullo diventava `{ totp: [] }`, letto come «non ha il codice»: il
     titolare finiva sull'attivazione di un codice nuovo. Ora un errore è un errore: prima i
     fattori della sessione, poi due tentativi in più, poi «non disponibile», e il cancello
     dice cosa fare. */
  const refreshAal = useCallback(async () => {
    const mia = ++richiestaMfa.current;
    if (!mfaNoto.current) setMfaStato(MFA.IN_CORSO);
    for (let tentativo = 1; ; tentativo += 1) {
      try {
        const livello = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        let fattori = await supabase.auth.mfa.listFactors();
        if (fattori.error) fattori = await fattoriDellaSessione(fattori);
        // La lista dei fattori decide il cancello: senza, non si sa niente.
        if (livello.error || fattori.error || !fattori.data) {
          throw livello.error || fattori.error || new Error("fattori non arrivati");
        }
        if (mia !== richiestaMfa.current) return;
        setAal(livello.data || null);
        setFactors(fattori.data);
        mfaNoto.current = true;
        setMfaStato(MFA.DISPONIBILE);
        return;
      } catch (e) {
        if (mia !== richiestaMfa.current) return;
        if (tentativo < 3) {
          await new Promise((r) => setTimeout(r, ATTESA_RIPROVA_MS * tentativo));
          continue;
        }
        if (mfaNoto.current) return; // rivalidazione fallita: si tiene l'ultima verità nota
        setAal(null);
        setFactors({ totp: [] });
        setMfaStato(MFA.NON_DISPONIBILE);
        return;
      }
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    const mia = ++richiesta.current;
    if (!profiloNoto.current) setProfiloStato(PROFILO.IN_CORSO);
    try {
      const res = await api.get("/me");
      if (mia !== richiesta.current) return; // una richiesta più recente ha già risposto
      const p = res?.data?.profile ?? null;
      if (p) {
        setProfile(p);
        profiloNoto.current = true;
        setProfiloStato(PROFILO.DISPONIBILE);
      } else if (!profiloNoto.current) {
        setProfile(null);
        setProfiloStato(PROFILO.NON_DISPONIBILE);
      }
    } catch (e) {
      if (mia !== richiesta.current) return;
      if (profiloNoto.current) return; // rivalidazione fallita: si tiene l'ultima verità nota
      setProfile(null);
      setProfiloStato(PROFILO.NON_DISPONIBILE);
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((ev, s) => {
      // L'uscita da un'altra scheda (o la sessione che si chiude) arriva qui: chiude anche il cookie dei listini.
      if (ev === "SIGNED_OUT") chiudiListini();
      setSession(s);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session) {
      richiesta.current += 1; // una `/me` ancora in volo non parla più per noi
      richiestaMfa.current += 1;
      profiloNoto.current = false; // sessione nuova, verità nuova: si riparte da zero
      mfaNoto.current = false;
      setProfile(null);
      setProfiloStato(PROFILO.INATTIVO);
      setAal(null);
      setFactors({ totp: [] });
      setMfaStato(MFA.INATTIVO);
      return;
    }
    refreshAal();
    refreshProfile();
  }, [session, refreshAal, refreshProfile]);

  const signOut = useCallback(async () => {
    richiesta.current += 1;
    richiestaMfa.current += 1;
    profiloNoto.current = false;
    mfaNoto.current = false;
    chiudiListini(); // il cookie dei listini sta su un'altra origine: non lo toglie il logout di Supabase
    await supabase.auth.signOut();
    setSession(null);
    setProfile(null);
    setProfiloStato(PROFILO.INATTIVO);
    setMfaStato(MFA.INATTIVO);
  }, []);

  const value = useMemo(
    () => ({
      session,
      profile,
      profiloStato,
      aal,
      factors,
      mfaStato,
      loading,
      refreshAal,
      refreshProfile,
      signOut,
    }),
    [session, profile, profiloStato, aal, factors, mfaStato, loading, refreshAal, refreshProfile, signOut],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
